import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';

// Define Organization type locally since mock auth doesn't have organizations
interface Organization {
  id: string;
  name: string;
  display_name?: string;
  metadata?: Record<string, unknown>;
}
import { Button } from '../ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/DropdownMenu';
import { Building2, ChevronDown, Check, Loader2 } from 'lucide-react';

interface OrganizationSwitcherProps {
  className?: string;
  showLabel?: boolean;
}

export function OrganizationSwitcher({ className, showLabel = true }: OrganizationSwitcherProps) {
  const auth = useAuth() as {user?: {id?: string; email?: string}; isLoading?: boolean; organizations?: Organization[]; currentOrganization?: Organization; switchOrganization?: (orgId: string) => void};
  const { user, isLoading } = auth;
  
  // Check if organization features are available (not in mock auth)
  const organization = auth.organization;
  const switchOrganization = auth.switchOrganization;
  const getUserOrganizations = auth.getUserOrganizations;
  
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);

  // Load user's organizations
  useEffect(() => {
    const loadOrganizations = async () => {
      if (!user || !getUserOrganizations) return;
      
      try {
        setLoadingOrgs(true);
        const userOrgs = await getUserOrganizations();
        setOrganizations(userOrgs);
      } catch (error) {
        console.error('Failed to load organizations:', error);
      } finally {
        setLoadingOrgs(false);
      }
    };

    loadOrganizations();
  }, [user, getUserOrganizations]);

  // Handle organization switch
  const handleSwitchOrganization = async (orgId: string) => {
    if (orgId === organization?.id || switchingTo || !switchOrganization) return;

    try {
      setSwitchingTo(orgId);
      await switchOrganization(orgId);
      
      // Refresh the page to ensure all components get the new context
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch organization:', error);
      setSwitchingTo(null);
    }
  };

  // Don't render if user has no organizations or only one organization
  if (!user || organizations.length <= 1) {
    return null;
  }

  const currentOrg = organization || organizations[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={`h-auto p-2 justify-start ${className}`}
          disabled={isLoading || loadingOrgs}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 flex-shrink-0" />
            {showLabel && (
              <div className="flex flex-col items-start min-w-0">
                <span className="text-sm font-medium truncate max-w-[150px]">
                  {currentOrg?.name || 'Select Organization'}
                </span>
                {currentOrg?.plan && (
                  <span className="text-xs text-muted-foreground capitalize">
                    {currentOrg.plan} Plan
                  </span>
                )}
              </div>
            )}
            {loadingOrgs ? (
              <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
            ) : (
              <ChevronDown className="h-3 w-3 flex-shrink-0" />
            )}
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Switch Organization</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {organizations.map((org) => (
          <DropdownMenuItem
            key={org.id}
            onClick={() => handleSwitchOrganization(org.id)}
            disabled={switchingTo === org.id}
            className="flex items-center justify-between"
          >
            <div className="flex flex-col items-start min-w-0 flex-1">
              <span className="font-medium truncate max-w-[180px]">
                {org.name}
              </span>
              <span className="text-xs text-muted-foreground capitalize">
                {org.plan} Plan • {org.status}
              </span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {switchingTo === org.id && (
                <Loader2 className="h-3 w-3 animate-spin" />
              )}
              {org.id === currentOrg?.id && !switchingTo && (
                <Check className="h-3 w-3" />
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default OrganizationSwitcher;